// chartConfig.js
export const getBarChartOptions = ({
  xAxis = [],
  series = [],
  maxLabelCharsPerLine = 10,
  isSmall = false,
} = {}) => ({
  title: {
    text: "Incident By Type",
    textStyle: { fontSize: 14, fontWeight: "normal", color: "#333" },
  },
  tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },

  legend: {
    type: "scroll",
    data: series.map((s) => s.name),
    bottom: 0,
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 8,
    textStyle: { fontSize: isSmall ? 8 : 10 },
  },

  grid: {
    left: 10,
    right: 10,
    bottom: isSmall ? 40 : 50,
    top: isSmall ? 45 : 50,
    containLabel: true,
  },

  xAxis: {
    type: "category",
    data: xAxis,
    axisLabel: {
      interval: 0,
      fontSize: isSmall ? 7 : 10,
      formatter: function (value) {
        if (!value) return value;
        if (value.length > maxLabelCharsPerLine) {
          return (
            value.slice(0, maxLabelCharsPerLine) +
            "\n" +
            value.slice(maxLabelCharsPerLine)
          );
        }
        return value;
      },
    },
  },

  yAxis: { type: "value" },

  series,
});

export const getLineChartOptions = ({
  xAxis = [],
  xAxisWithTooltip = [],
  open = [],
  truePositive = [],
  falsePositive = [],
  other = [],
} = {}) => ({
  title: {
    text: "Incidents By Status Over Time",
    textStyle: { fontSize: 14, fontWeight: "normal", color: "#333" },
  },
  tooltip: {
    trigger: "axis",
    formatter: (params) => {
      const xIndex = params[0].dataIndex;
      const dateTooltip = xAxisWithTooltip[xIndex]?.toolTip ?? "";
      let tooltip = `<b>${params[0].axisValue} ${dateTooltip}</b><br/>`;
      params.forEach((p) => {
        tooltip += `${p.marker}${p.seriesName}: ${p.data}<br/>`;
      });
      return tooltip;
    },
  },
  legend: {
    data: ["Open", "True Positive", "False Positive", "Other"],
    bottom: 0,
  },
  grid: { left: "3%", right: "4%", bottom: "15%", containLabel: true },
  xAxis: { type: "category", data: xAxis },
  yAxis: { type: "value" },
  series: [
    {
      name: "Open",
      type: "line",
      data: open,
      color: "#551fff",
      lineStyle: { width: 3 },
      symbol: "circle",
      symbolSize: 6,
    },
    {
      name: "True Positive",
      type: "line",
      data: truePositive,
      color: "#ff0000",
      lineStyle: { width: 3 },
      symbol: "circle",
      symbolSize: 6,
    },
    {
      name: "False Positive",
      type: "line",
      data: falsePositive,
      color: "#08b9fe",
      lineStyle: { width: 3 },
      symbol: "circle",
      symbolSize: 6,
    },
    {
      name: "Other",
      type: "line",
      data: other,
      color: "#e66b84",
      lineStyle: { width: 3 },
      symbol: "circle",
      symbolSize: 6,
    },
  ],
});

export const getDonutChartOptions = ({ status = [], isSmall = false } = {}) => {
  // Build a lookup map from status type to count
  const statusMap = {};
  status.forEach((s) => {
    statusMap[s.type] = s.count;
  });

  // Build the 'selected' config for legend
  const selected = {};
  status.forEach((s) => {
    if (s.type === "Closed - False Positive") {
      selected[s.type] = false; // Disable selection for this legend item
    }
  });

  return {
    title: {
      text: "Incident By Status",
      textStyle: { fontSize: 14, fontWeight: "normal", color: "#333" },
    },
    tooltip: { trigger: "item" },
    legend: {
      orient: "vertical",
      right: 10,
      top: "center",
      textStyle: { fontSize: isSmall ? 8.5 : 10 },
      formatter: (name) => {
        const count = statusMap[name] || 0;
        const maxLength = isSmall ? 20 : 32;
        let displayName = name;
        if (name.length > maxLength) {
          displayName = name.substring(0, maxLength - 3) + "...";
        }
        return `(${count}) ${displayName}`;
      },
      selected: selected,
    },
    series: [
      {
        name: "Status",
        type: "pie",
        radius: isSmall ? ["30%", "55%"] : ["40%", "70%"],
        center: isSmall ? ["35%", "50%"] : ["32%", "50%"],
        label: { show: false },
        emphasis: { label: { show: false } },
        labelLine: { show: false },
        data: status.map((s) => ({
          value: s.count,
          name: s.type,
        })),
      },
    ],
  };
};

export const getRadarChartOptions = ({
  detectionMethod = [],
  isSmall,
  excludeSIEM,
} = {}) => {
  console.log(excludeSIEM, "excludeSIEM");
  const fixedDetectionMethods = [
    "SIEM",
    "Customer Reported",
    "SysGuard",
    "Hunting",
    "Profile - Customer Daily Journey",
    "Sentinel One",
    "DataVision",
    "MS Defender / MS Sentinel",
  ];

  const allMethods = [
    ...fixedDetectionMethods,
    ...detectionMethod
      .map((dm) => dm.type)
      .filter((name) => !fixedDetectionMethods.includes(name)),
  ].filter((name) => !(excludeSIEM && name === "SIEM"));
  const dataMap = detectionMethod.reduce((acc, dm) => {
    acc[dm.type] = dm.count;
    return acc;
  }, {});

  const actualValues = allMethods.map((name) => dataMap[name] || 0);
  const logValues = actualValues.map((count) => Math.log10(count + 1));
  const maxVal = Math.max(...logValues, 1);

  const indicators = allMethods.map((name) => ({
    name,
    max: maxVal,
  }));

  return {
    // Removed title from here

    tooltip: {
      trigger: "item",
      formatter: (params) =>
        allMethods
          .map((name, i) => `${name}: ${actualValues[i]}`)
          .join("<br/>"),
    },
    radar: {
      indicator: indicators,
      shape: "polygon",
      splitNumber: 4,
      axisLine: { lineStyle: { color: "#ddd" } },
      splitLine: { lineStyle: { color: "#ddd" } },
      splitArea: {
        areaStyle: {
          color: ["rgba(52, 152, 219, 0.1)", "rgba(52, 152, 219, 0.05)"],
        },
      },
      name: {
        textStyle: {
          color: "gray",
          fontSize: 12,
        },
      },
      radius: isSmall ? "50%" : "70%",
    },
    series: [
      {
        name: "Detection Methods",
        type: "radar",
        data: [
          {
            value: logValues,
            rawValues: actualValues,
            name: "Current Period",
            itemStyle: { color: "#27ae60" },
            areaStyle: { color: "rgba(39, 174, 96, 0.3)" },
          },
        ],
      },
    ],
  };
};
